// Native Mobile App Utilities
// Detects native environment and provides platform-specific functionality

// Detect if running in Capacitor native app
export const isNativeApp = () => {
  return window.Capacitor !== undefined || 
         window.navigator.standalone === true ||
         window.matchMedia('(display-mode: standalone)').matches;
};

// Detect platform
export const getPlatform = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  if (/android/i.test(userAgent)) return 'android';
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) return 'ios';
  return 'web';
};

// Initialize native app features
export const initNativeApp = () => {
  const platform = getPlatform();
  const isNative = isNativeApp();
  
  // ONLY apply native-app class when actually running as a native app (Capacitor or PWA standalone mode)
  // Do NOT apply to regular mobile browsers
  if (isNative) {
    // Add native-app class to body
    document.body.classList.add('native-app');
    document.body.classList.add(platform);
    
    // Handle iOS status bar
    if (platform === 'ios') {
      const meta = document.createElement('meta');
      meta.name = 'apple-mobile-web-app-status-bar-style';
      meta.content = 'black-translucent';
      document.head.appendChild(meta);
    }
    
    // Handle Android status bar color
    if (platform === 'android') {
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) {
        meta.content = '#0B2821';
      }
    }
    
    // Disable zoom on mobile (only for native apps)
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (viewportMeta) {
      viewportMeta.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';
    }
    
    // Handle keyboard events
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        const keyboardHeight = window.innerHeight - window.visualViewport.height;
        document.documentElement.style.setProperty('--keyboard-height', `${keyboardHeight}px`);
        
        if (keyboardHeight > 100) {
          document.body.classList.add('keyboard-open');
        } else {
          document.body.classList.remove('keyboard-open');
        }
      });
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Compass] Native app initialized - Platform: ${platform}`);
    }
  }
  
  return { isNative, platform };
};

// Haptic feedback (if available)
export const hapticFeedback = (type = 'light') => {
  if (window.Capacitor?.Plugins?.Haptics) {
    const { Haptics, ImpactStyle } = window.Capacitor.Plugins;
    
    switch (type) {
      case 'light':
        Haptics.impact({ style: ImpactStyle.Light });
        break;
      case 'medium':
        Haptics.impact({ style: ImpactStyle.Medium });
        break;
      case 'heavy':
        Haptics.impact({ style: ImpactStyle.Heavy });
        break;
      case 'success':
        Haptics.notification({ type: 'SUCCESS' });
        break;
      case 'warning':
        Haptics.notification({ type: 'WARNING' });
        break;
      case 'error':
        Haptics.notification({ type: 'ERROR' });
        break;
      default:
        Haptics.impact({ style: ImpactStyle.Light });
    }
  } else if (navigator.vibrate) {
    // Fallback to Web Vibration API
    const patterns = {
      light: [10],
      medium: [20],
      heavy: [30],
      success: [10, 50, 10],
      warning: [20, 50, 20],
      error: [30, 50, 30, 50, 30]
    };
    navigator.vibrate(patterns[type] || patterns.light);
  }
};

// Share content natively
export const shareContent = async ({ title, text, url }) => {
  try {
    if (window.Capacitor?.Plugins?.Share) {
      await window.Capacitor.Plugins.Share.share({ title, text, url });
      return true;
    } else if (navigator.share) {
      await navigator.share({ title, text, url });
      return true;
    }
    return false;
  } catch (error) {
    // Share failed silently - error already handled by returning false
    return false;
  }
};

// Open external URL in browser
export const openExternalUrl = (url) => {
  if (window.Capacitor?.Plugins?.Browser) {
    window.Capacitor.Plugins.Browser.open({ url });
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

// Get safe area insets
export const getSafeAreaInsets = () => {
  const style = getComputedStyle(document.documentElement);
  return {
    top: parseInt(style.getPropertyValue('--safe-area-inset-top') || '0'),
    bottom: parseInt(style.getPropertyValue('--safe-area-inset-bottom') || '0'),
    left: parseInt(style.getPropertyValue('--safe-area-inset-left') || '0'),
    right: parseInt(style.getPropertyValue('--safe-area-inset-right') || '0')
  };
};

// Check if PWA can be installed
export const canInstallPWA = () => {
  return window.deferredPrompt !== undefined;
};

// Trigger PWA install prompt
export const installPWA = async () => {
  if (window.deferredPrompt) {
    window.deferredPrompt.prompt();
    const { outcome } = await window.deferredPrompt.userChoice;
    window.deferredPrompt = null;
    return outcome === 'accepted';
  }
  return false;
};

// Handle app lifecycle events (Capacitor)
export const setupAppLifecycle = (callbacks = {}) => {
  if (window.Capacitor?.Plugins?.App) {
    const { App } = window.Capacitor.Plugins;
    
    // App state change (foreground/background)
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive && callbacks.onResume) {
        callbacks.onResume();
      } else if (!isActive && callbacks.onPause) {
        callbacks.onPause();
      }
    });
    
    // Back button (Android)
    App.addListener('backButton', ({ canGoBack }) => {
      if (callbacks.onBackButton) {
        callbacks.onBackButton(canGoBack);
      } else if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });
    
    // Deep links / URL opens
    App.addListener('appUrlOpen', ({ url }) => {
      if (callbacks.onDeepLink) {
        callbacks.onDeepLink(url);
      }
    });
  }
};

export default {
  isNativeApp,
  getPlatform,
  initNativeApp,
  hapticFeedback,
  shareContent,
  openExternalUrl,
  getSafeAreaInsets,
  canInstallPWA,
  installPWA,
  setupAppLifecycle
};
